import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { CandidateTabs } from "../../components/candidate/CandidateTabs";

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-stone-sub mb-1">{label}</label>
    {children}
  </div>
);
const input = "w-full px-4 py-3 rounded-xl border border-stone-border focus:outline-none focus:ring-2 focus:ring-brand bg-white";

const Check = ({ testid, label, checked, onChange }) => (
  <label className="flex items-center gap-3 cursor-pointer bg-white border border-stone-border rounded-xl px-4 py-3">
    <input data-testid={testid} type="checkbox" checked={checked} onChange={onChange} className="w-5 h-5 accent-[#D95A41]" />
    <span className="text-sm font-medium text-stone-sub">{label}</span>
  </label>
);

export default function JobRequest() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    desired_position: "", any_position: false, areas: "", has_experience: false,
    speaks_english: false, seeks_housing: false, willing_to_relocate: false,
    city: "", state: "", zip_code: "", description: "", phone: user?.phone || "",
  });

  useEffect(() => {
    api.get("/job-requests/mine").then(({ data }) => {
      if (data) {
        setForm({
          desired_position: data.desired_position || "",
          any_position: !!data.any_position,
          areas: (data.areas || []).join(", "),
          has_experience: !!data.has_experience,
          speaks_english: !!data.speaks_english,
          seeks_housing: !!data.seeks_housing,
          willing_to_relocate: !!data.willing_to_relocate,
          city: data.city || "", state: data.state || "", zip_code: data.zip_code || "",
          description: data.description || "", phone: data.phone || user?.phone || "",
        });
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/job-requests", {
        ...form,
        areas: form.areas.split(",").map((a) => a.trim()).filter(Boolean),
      });
      toast.success(t("candidate.request.saved"));
    } catch (err) {
      toast.error(t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-16 text-center text-stone-muted">{t("jobs.loading")}</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading text-3xl font-bold text-stone-text mb-1">{t("candidate.request.title")}</h1>
      <p className="text-stone-muted mb-6">{t("candidate.request.subtitle")}</p>
      <CandidateTabs />

      <form onSubmit={save} className="bg-white rounded-2xl border border-stone-border shadow-sm p-6 sm:p-8 space-y-4" data-testid="request-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("candidate.request.desiredPosition")}>
            <input data-testid="request-position" className={input} value={form.desired_position} onChange={set("desired_position")} />
          </Field>
          <Field label={t("candidate.request.areas")}>
            <input data-testid="request-areas" className={input} value={form.areas} onChange={set("areas")} placeholder="Cocina, Parrilla" />
          </Field>
          <Field label={t("candidate.request.city")}>
            <input data-testid="request-city" className={input} value={form.city} onChange={set("city")} />
          </Field>
          <Field label={t("candidate.request.state")}>
            <input data-testid="request-state" className={input} value={form.state} onChange={set("state")} />
          </Field>
          <Field label={t("candidate.request.zip")}>
            <input data-testid="request-zip" className={input} value={form.zip_code} onChange={set("zip_code")} />
          </Field>
          <Field label={t("candidate.request.phone")}>
            <input data-testid="request-phone" className={input} value={form.phone} onChange={set("phone")} />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Check testid="request-any" label={t("candidate.request.anyPosition")} checked={form.any_position} onChange={toggle("any_position")} />
          <Check testid="request-experience" label={t("candidate.request.hasExperience")} checked={form.has_experience} onChange={toggle("has_experience")} />
          <Check testid="request-english" label={t("candidate.request.speaksEnglish")} checked={form.speaks_english} onChange={toggle("speaks_english")} />
          <Check testid="request-housing" label={t("candidate.request.seeksHousing")} checked={form.seeks_housing} onChange={toggle("seeks_housing")} />
          <Check testid="request-relocate" label={t("candidate.request.willingToRelocate")} checked={form.willing_to_relocate} onChange={toggle("willing_to_relocate")} />
        </div>

        <Field label={t("candidate.request.description")}>
          <textarea data-testid="request-description" rows={4} className={input} value={form.description} onChange={set("description")} placeholder={t("candidate.request.descriptionPlaceholder")} />
        </Field>

        <button data-testid="request-save" type="submit" disabled={saving} className="px-6 py-3 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors disabled:opacity-60">
          {saving ? "..." : t("candidate.request.save")}
        </button>
      </form>
    </div>
  );
}
